/**
 * UniquenessVisuals.js
 * Visual components for the Uniqueness Dashboard
 * Theme: Biometric/Identity (Fingerprints & DNA)
 */
import React from 'react';
import { useTheme } from '../../../utils/ThemeSwitcher';

// 1. DYNAMIC FINGERPRINT GAUGE
export const FingerprintGauge = ({ score }) => {
  const { isDarkMode } = useTheme();
  const color = score > 98 ? '#10B981' : score > 90 ? '#38B6FF' : '#EF4444';
  const bgColor = isDarkMode ? '#374151' : '#E5E7EB';
  
  // Create a fingerprint-like path (simplified concentric arcs)
  const arcs = [
    "M 50 50 m -10 0 a 10 10 0 1 0 20 0 a 10 10 0 1 0 -20 0",
    "M 50 50 m -18 0 a 18 18 0 1 0 36 0 a 18 18 0 1 0 -36 0",
    "M 50 50 m -26 0 a 26 26 0 1 0 52 0 a 26 26 0 1 0 -52 0",
    "M 50 50 m -34 0 a 34 34 0 1 0 68 0 a 34 34 0 1 0 -68 0",
  ];

  // Calculate dash array to animate the "fill" based on score
  const totalLength = 250; // Approx length of paths
  const fillLength = (score / 100) * totalLength;

  return (
    <div style={{ position: 'relative', width: '120px', height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width="120" height="120" viewBox="0 0 100 100">
        {/* Background Tracks */}
        {arcs.map((d, i) => (
          <path key={`bg-${i}`} d={d} fill="none" stroke={bgColor} strokeWidth="6" strokeLinecap="round" opacity="0.3" />
        ))}
        
        {/* Active Fill Tracks (Animated) */}
        {arcs.map((d, i) => (
          <path 
            key={`fill-${i}`} 
            d={d} 
            fill="none" 
            stroke={color} 
            strokeWidth="6" 
            strokeLinecap="round"
            strokeDasharray={`${fillLength} 1000`} 
            style={{ transition: 'all 1s ease-out' }}
          />
        ))}
        
        {/* Center Percentage */}
        <text 
          x="50" y="55" 
          textAnchor="middle" 
          fill={isDarkMode ? '#fff' : '#111827'} 
          fontSize="18" 
          fontWeight="bold"
        >
          {score}%
        </text>
      </svg>
      
      {/* Glow Effect */}
      <div style={{
        position: 'absolute', inset: 0, borderRadius: '50%',
        boxShadow: `0 0 30px ${color}40`, pointerEvents: 'none'
      }} />
    </div>
  );
};

// 2. DNA STRAND BAR (Visualizes Duplicates vs Unique)
export const DNAStrandBar = ({ uniqueCount, duplicateCount, onFilterAction }) => {
  const total = uniqueCount + duplicateCount || 1;
  const dupPercent = (duplicateCount / total) * 100;
  
  return (
    <div style={{ width: '100%', height: '24px', display: 'flex', gap: '4px', alignItems: 'center' }}>
      {Array.from({ length: 20 }).map((_, i) => {
        const isDuplicate = (i / 20) * 100 < dupPercent;
        return (
          <div 
            key={i}
            onClick={() => isDuplicate && onFilterAction && onFilterAction()}
            style={{
              flex: 1,
              height: isDuplicate ? '24px' : '12px',
              backgroundColor: isDuplicate ? '#EF4444' : '#38B6FF',
              borderRadius: '4px',
              opacity: isDuplicate ? 1 : 0.4,
              cursor: isDuplicate ? 'pointer' : 'default',
              transform: isDuplicate ? 'scaleY(1.1)' : 'scaleY(1)',
              transition: 'all 0.2s ease',
            }}
            className={isDuplicate ? 'dna-segment-active' : ''}
            title={isDuplicate ? 'Click to view duplicate clusters' : 'Unique Record'}
          />
        );
      })}
    </div>
  );
};

// 3. DATAQUAD INSIGHT BUBBLE
export const InsightBubble = ({ duplicateCount, issueType }) => {
  const { isDarkMode } = useTheme();
  
  // Decide expression
  const isHappy = duplicateCount === 0;
  const isWorried = duplicateCount > 50;
  
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      padding: '16px 24px',
      background: isDarkMode 
        ? 'linear-gradient(90deg, rgba(56, 182, 255, 0.1), transparent)' 
        : 'linear-gradient(90deg, rgba(56, 182, 255, 0.05), transparent)',
      borderLeft: '4px solid #38B6FF',
      borderRadius: '8px',
      marginBottom: '24px'
    }}>
      <div style={{ fontSize: '24px' }}>
        {isHappy ? '🎉' : isWorried ? '🧐' : '🔎'}
      </div>
      <div>
        <h4 style={{ margin: 0, fontSize: '14px', color: '#38B6FF', textTransform: 'uppercase', letterSpacing: '1px' }}>
          Detective's Analysis
        </h4>
        <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: isDarkMode ? '#E5E7EB' : '#374151' }}>
          {isHappy 
            ? "Immaculate! No duplicates detected in this dataset."
            : `I've found ${duplicateCount} records that look suspicious. ${isWorried ? 'This is higher than usual.' : 'Check the clusters below.'}`
          }
        </p>
      </div>
    </div>
  );
};